import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import { RootState } from 'redux/schemas';
import { Course } from 'redux/schemas/models/course';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getCourseRoleplayUsage, setCourseRoleplayUsage, toUsageValue } from 'redux/actions/ai-roleplay-usage';
import { getCourseRoleplayAccessSettings } from 'redux/selectors/course';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import LoadingWrapper from 'shared/components/loading-wrapper';
import RoleplayUsageManagement from 'roleplay/course-level-management/roleplay-usage-management';
import useRoleplayUsageForm, { RoleplayUsageValue, defaultRoleplayUsageValue } from 'roleplay/course-level-management/use-roleplay-usage-form';

const usageManagementTags = config.pendo.activities.aiRolePlay.usageManagement;

const courseTypeLabel = (course: Course): string => {
  const { COURSE_TYPE } = t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.EDIT_MODAL;
  if (course.isPrimary) {
    return COURSE_TYPE.PRIMARY_COURSE();
  }
  if (course.isCohort) {
    return COURSE_TYPE.COHORT();
  }
  return COURSE_TYPE.INDIVIDUAL_COURSE();
};

type EditRoleplayUsageModalProps = {
  course: Course,
  show: boolean,
  onClose: () => void,
};

const EditRoleplayUsageModal = ({ course, show, onClose }: EditRoleplayUsageModalProps) => {
  const dispatch = useAppDispatch();
  const { value, setValue, onChange, validate, error } = useRoleplayUsageForm(defaultRoleplayUsageValue);
  // The saved server value, used as the baseline for the dirty check.
  const settings = useSelector((state: RootState) => getCourseRoleplayAccessSettings(state, course.catalogId));
  const persistedValue = useMemo(
    () => (settings ? toUsageValue(settings) : defaultRoleplayUsageValue),
    [settings],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load the course's current setting each time the modal opens.
  useEffect(() => {
    if (!show) {
      return;
    }

    setIsLoading(true);
    wrapThunkAction<RoleplayUsageValue>(dispatch(getCourseRoleplayUsage(course.catalogId)))
      .then((action) => setValue(action.payload))
      .catch(() => { /* error surfaced via AlertMessages in the thunk */ })
      .finally(() => setIsLoading(false));
  }, [show, course.catalogId, dispatch, setValue]);

  const isDirty = !isEqual(value, persistedValue);

  const handleUpdate = () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    wrapThunkAction(dispatch(setCourseRoleplayUsage({ catalogId: course.catalogId, value })))
      .then(() => {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.EDIT_MODAL.SAVE_SUCCESS(),
        }));
        onClose();
      })
      .catch(() => { /* error surfaced via AlertMessages in the thunk */ })
      .finally(() => setIsSaving(false));
  };

  return (
    <NvModal
      type={ModalType.DYNAMIC}
      width={600}
      fullHeight={false}
      show={show}
      onClose={onClose}
      header={t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.EDIT_MODAL.HEADER()}
      body={(
        <div className='p-4'>
          <div className='px-4 py-2 mb-4 border border-gray-5 bg-gray-7 d-flex align-items-center justify-content-between'>
            <div>
              <div className='text-small text-gray-2 mb-1'>
                {t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.EDIT_MODAL.COURSE_NAME_LABEL()}
              </div>
              <div className='text-small font-weight-bold'>{course.name}</div>
            </div>
            <div className='text-small'>{courseTypeLabel(course)}</div>
          </div>
          <LoadingWrapper isLoaded={!isLoading} keepRendered={false}>
            <RoleplayUsageManagement
              value={value}
              onChange={onChange}
              error={error}
            />
          </LoadingWrapper>
          <div className='d-flex justify-content-center mt-6'>
            <Button variant='secondary' className='mr-2' onClick={onClose}>
              {t.FORM.CANCEL()}
            </Button>
            <Button
              variant='primary'
              disabled={!isDirty || isLoading || isSaving}
              onClick={handleUpdate}
              data-qa={usageManagementTags.editSave}
            >
              {t.FORM.UPDATE()}
            </Button>
          </div>
        </div>
      )}
    />
  );
};

export default EditRoleplayUsageModal;
